import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../../utils/api.js';
import { toast } from 'react-hot-toast';

/**
 * InvitationVerification Component
 * Handles invitation code verification before registration
 */
const InvitationVerification = () => {
  const [invitationCode, setInvitationCode] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Check for code in URL query parameters on load
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const codeParam = params.get('code');
    const emailParam = params.get('email');
    
    if (codeParam) {
      setInvitationCode(codeParam);
      console.log('Code from URL:', codeParam);
    }
    
    if (emailParam) {
      setEmail(emailParam);
      console.log('Email from URL:', emailParam);
    }
    
    // If both params exist, auto-verify
    if (codeParam && emailParam) {
      handleVerifyInvitation(codeParam, emailParam);
    }
  }, [location]);

  // Validate invitation code with the server
  const handleVerifyInvitation = async (code = invitationCode, emailToUse = email) => {
    if (!code) {
      setError('Please enter an invitation code');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Force absolute API base URL based on hostname
      const host = window.location.hostname;
      let apiBase = '';
      if (host.includes('staging')) {
        apiBase = 'https://api.staging.mnymny.com';
      } else if (host.includes('mnymny.com')) {
        apiBase = 'https://api.mnymny.com';
      } else {
        // Local dev - use api instance baseURL or empty for proxy
        apiBase = (api && api.defaults && api.defaults.baseURL) ? api.defaults.baseURL : '';
      }
      
      const endpoint = apiBase ? `${apiBase}/api/invitations/validate` : '/api/invitations/validate';

      // Log the verification attempt
      console.log('Verifying invitation:', { code, email: emailToUse, endpoint, apiBase, host });
      
      // Make API request to validate the invitation code (absolute URL if available)
      const response = await api.post(endpoint, {
        code,
        email: emailToUse
      }, { baseURL: '' });

      console.log('Verification response:', response.data);

      if (response.data.success) {
        setVerificationSuccess(true);
        toast.success('Invitation code verified successfully!');
        
        try {
          // Store the verified code/email in session storage for the registration flow
          // Store as JSON object instead of separate items to match what the Register component expects
          const invitationData = {
            code,
            email: emailToUse
          };
          
          // Clear any existing session data first
          sessionStorage.removeItem('invitationData');
          
          // Store new invitation data
          sessionStorage.setItem('invitationData', JSON.stringify(invitationData));
          
          // Log for debugging
          console.log('Storing invitation data in session storage:', invitationData);
          console.log('Session storage after storing:', sessionStorage.getItem('invitationData'));
          
          // Redirect to registration page with shorter delay and forced reload
          setTimeout(() => {
            console.log('Redirecting to registration page...');
            // Use a direct location change instead of React Router for a full page load
            window.location.replace('/register');
          }, 1000);
        } catch (storageError) {
          console.error('Error storing invitation data:', storageError);
          // Still try to redirect despite storage error
          setTimeout(() => {
            window.location.replace('/register');
          }, 1000);
        }
      } else {
        setError(response.data.message || 'Invalid invitation code');
      }
    } catch (err) {
      console.error('Invitation verification error:', err);
      setError(err.response?.data?.message || 'Failed to verify invitation code');
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    handleVerifyInvitation();
  };

  return (
    <div className="min-h-screen flex flex-col justify-center items-center bg-gradient-to-br from-[#6C5DD3] to-[#8247E5] relative">
      {/* White overlay for faded effect */}
      <div className="absolute inset-0 bg-white opacity-25"></div>
      {/* Content */}
      <div className="relative z-10 bg-white bg-opacity-95 backdrop-blur-sm p-8 rounded-3xl shadow-xl w-full max-w-md mx-4 my-8">
        <h2 className="text-2xl font-semibold text-center mb-2 text-[#6C5DD3]">
          Verify Invitation
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600 mb-6">
          Enter your invitation code to access TheWallet
        </p>

        {verificationSuccess ? (
          <div className="p-4 bg-green-50 text-green-800 rounded-md">
            <p className="text-center">
              Invitation code verified successfully!
              <br />
              Redirecting to registration...
            </p>
          </div>
        ) : (
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="p-4 bg-red-50 text-red-800 rounded-md">
                {error}
              </div>
            )}
            
            <div>
              <label htmlFor="invitation-code" className="block text-sm font-medium text-gray-700">
                Invitation Code
              </label>
              <div className="mt-1">
                <input
                  id="invitation-code"
                  name="invitation-code"
                  type="text"
                  value={invitationCode}
                  onChange={(e) => setInvitationCode(e.target.value.toUpperCase())}
                  required
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900"
                  placeholder="e.g. ABCD-1234"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address <span className="text-gray-400">(optional)</span>
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900"
                  placeholder="your@email.com"
                />
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Required if the invitation was sent to a specific email address
              </p>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  loading ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
              >
                {loading ? 'Verifying...' : 'Verify Invitation'}
              </button>
            </div>
            
            <div className="mt-6">
              <p className="text-center text-sm text-gray-600">
                Don't have an invitation code?{' '}
                <a
                  href="/request-invitation"
                  className="font-medium text-indigo-600 hover:text-indigo-500"
                >
                  Request an invitation
                </a>
              </p>
            </div>
          </form>
        )}
      </div>
      <div className="mt-6 text-center text-white text-xs opacity-80">
        © 2024 O Industries LLC. All rights reserved.
      </div>
    </div>
  );
};

export default InvitationVerification; 